/** Employer option lists, plus the header-bridge event names for the Employers
 *  screen.
 *
 *  Event names live here rather than in a separate module because both sides of
 *  the bridge (the global header / AuthenticatedController and the employers
 *  feature) already import this file for the status options, so co-locating them
 *  costs no extra import and removes the untyped string literals that are this
 *  codebase's most common bridge bug. Same reasoning as
 *  src/features/users/userRoles.ts. */

import type { EmployerStatus, EmployerStatusFilter, MainContractorEntityType } from './employerLabels';
import { ENTITY_OPTIONS, getEntityLabel, STATUS_OPTIONS } from './employerLabels';

export type { EmployerStatus, EmployerStatusFilter };

export { ENTITY_OPTIONS, getEntityLabel, STATUS_OPTIONS };

/** Entity types that can hold a Companies House registration. An individual,
 *  a sole trader and a public body have no Companies House record,
 *  so neither the CH risk view nor an Experian report has anything to look up.
 *  Partnerships DO qualify: an LLP is registered at Companies House with an OC
 *  number and has charges, filings and members like any company. Public bodies
 *  do not: councils and NHS trusts are not on the register.
 *  A missing type is treated as registrable: the form defaults to a limited
 *  company, so an unset value means "not recorded yet", not "not a company".
 *
 *  This MUST stay in step with which entity types the Details form and the add
 *  form actually offer the Company Number field for. If they disagree, one side
 *  tells the user to record a number the other side gives them nowhere to type. */
export const canHaveCompanyNumber = (entityType?: string) =>
  !entityType || entityType === 'limited_company' || entityType === 'partnership';

export const getLegalNameLabel = (entityType: MainContractorEntityType) => {
  switch (entityType) {
    case 'individual':   return 'Homeowner / Individual Name *';
    case 'sole_trader':  return 'Sole Trader Name *';
    case 'public_body':  return 'Organisation Name *';
    default:             return 'Legal / Registered Name *';
  }
};


// ─── Status filter ────────────────────────────────────────────────────────────

export const EMPLOYER_STATUS_FILTER_ALL = 'all';

/** One canonical option list so the header pill, the in-page fallback dropdown
 *  and the native toolbar menu can never disagree. */
export const EMPLOYER_STATUS_FILTER_OPTIONS: Array<{ value: EmployerStatusFilter; label: string }> = [
  { value: EMPLOYER_STATUS_FILTER_ALL, label: 'All Employers' },
  ...STATUS_OPTIONS,
];

export function matchesEmployerStatusFilter(status: string | null | undefined, filter: string): boolean {
  return !filter || filter === EMPLOYER_STATUS_FILTER_ALL || status === filter;
}

// ─── Header bridge ────────────────────────────────────────────────────────────

/** Header -> list intents. */
export const EMPLOYERS_SEARCH_EVENT = 'employers:search';   // { value: string }
export const EMPLOYERS_FILTER_EVENT = 'employers:filter';   // { status: EmployerStatusFilter }
export const EMPLOYERS_CREATE_EVENT = 'employers:create';   // no detail

/** Header / sidebar -> screen. Selecting an employer replaces the directory with
 *  its profile, so the back chevron needs a way to undo that. */
export const EMPLOYERS_CLEAR_SELECTION_EVENT = 'employers:clear-selection';

/** Router -> screen: `{ id, section? }`. Clicking a row selects through the
 *  list's own callback, so this exists purely for the URL — a deeplink or a
 *  Back into /employers/[slug]/[section] has to restore the same profile AND
 *  the same page. */
export const EMPLOYERS_SELECT_EVENT = 'employers:select';

/** Screen -> header sync: `{ listVisible, statusFilter, selectedName }` while
 *  the screen is mounted, `null` once it isn't.
 *
 *  `listVisible` is false while a profile or the add form owns the content area.
 *  It is NOT the same as "EmployerList is mounted": search / filter / Add must
 *  never point at a directory nobody can see, and the drill-in means that state
 *  is owned by EmployerManagement, not by the list. */
export const EMPLOYERS_STATE_EVENT = 'employers:state-change';

/** Prefix for the macOS/iPad toolbar filter menu item ids. Taps arrive back on
 *  `quantis:native:menuAction`, so the publisher and the consumer have to agree
 *  on the string. */
export const EMPLOYERS_NATIVE_FILTER_PREFIX = 'emp-filter:';

/** Add/edit form -> header and sidebar sync:
 *  `{ isEdit, isSubmitting, hasChanges, activeSection, sectionIds }`, `null` on
 *  unmount.
 *
 *  `sectionIds` is the form's CURRENTLY VISIBLE pages, in order, and is the one
 *  thing the profile's equivalent broadcast does not need: Credit Check exists
 *  only while there is a company to check, so the sidebar cannot hold a fixed
 *  set of rows. Sending the surviving ids rather than the condition keeps the
 *  form the only place that decides which pages exist. */
export const EMPLOYER_FORM_STATE_EVENT = 'employer-form:state-change';
/** Header -> form intents. Back and cancel are the same action (both discard
 *  and return to the directory); they exist as one event because one is the
 *  leading navigation chevron and the other the trailing text pill. The wire
 *  string was `employer-form:close` and is deliberately renamed: `cancel` is
 *  what the pill says and what the intent means. */
export const EMPLOYER_FORM_SAVE_EVENT = 'employer-form:save';
export const EMPLOYER_FORM_CANCEL_EVENT = 'employer-form:cancel';

/** Sidebar -> add/edit form. Same reasoning as the profile's section event
 *  below: a row selection swaps which page of the form is mounted rather than
 *  scrolling one long document. Deliberately NOT routed through the app router
 *  like the profile's sections are — the form is transient and its page is not
 *  in the URL. */
export const EMPLOYER_FORM_SET_SECTION_EVENT = 'employer-form:set-section';

/** The form's pages, in sidebar order. Ids must match the rows
 *  buildEmployerFormSidebarTabItems builds — same hand-pairing as the profile's
 *  list below, and for the same reason (sidebarNavConfig imports nothing from
 *  features). This is the full set, not the visible one: 'credit' drops out
 *  unless there is a company to check, which is what EMPLOYER_FORM_STATE_EVENT's
 *  `sectionIds` carries. */
export const EMPLOYER_FORM_SECTION_IDS = [
  'company',
  'address',
  'contact',
  'invoicing',
  'notes',
  'people',
  'credit',
] as const;

export type EmployerFormSectionId = typeof EMPLOYER_FORM_SECTION_IDS[number];

/** Labels for the in-page section picker the form falls back to wherever neither
 *  sidebar draws its rows (iPhone shell, and touch-runtime web below lg). */
export const EMPLOYER_FORM_SECTION_LABELS: Record<EmployerFormSectionId, string> = {
  'company':   'Company',
  'address':   'Address',
  'contact':   'Contact',
  'invoicing': 'Invoicing',
  'notes':     'Notes',
  'people':    'Key Contacts',
  'credit':    'Credit Check',
};

export function isEmployerFormSectionId(value: unknown): value is EmployerFormSectionId {
  return typeof value === 'string'
    && (EMPLOYER_FORM_SECTION_IDS as readonly string[]).includes(value);
}

/** Sidebar -> profile. Each section is its own page, so a row selection swaps
 *  the profile's content rather than scrolling one document. */
export const EMPLOYER_DETAIL_SET_SECTION_EVENT = 'employer-detail:set-section';

/** Profile -> header: `{ isSubmitting, hasChanges, email, phone }`, `null` on
 *  unmount. The profile's own actions (Email, Call, and the Cancel/Save pair that
 *  appears once something has changed) all live in the global header, so the
 *  header needs to know which apply and the addresses to act on. */
export const EMPLOYER_DETAIL_STATE_EVENT = 'employer-detail:state-change';
/** Header -> profile intents for that same set. No edit intent: the profile has
 *  no edit mode to enter. */
export const EMPLOYER_DETAIL_CANCEL_EVENT = 'employer-detail:cancel';
export const EMPLOYER_DETAIL_SAVE_EVENT = 'employer-detail:save';

/** The profile's pages. Ids must match the rows
 *  buildEmployerDetailSidebarTabItems builds — sidebarNavConfig is a pure
 *  config module that imports nothing from features, so that pairing is by
 *  hand. The guard below is what stops a drifted id silently blanking the
 *  panel: an unknown section leaves the current page up instead. */
export const EMPLOYER_DETAIL_SECTION_IDS = [
  'details',
  'trading-history',
  'risk-profile',
  'notes',
  'contacts',
] as const;

export type EmployerDetailSectionId = typeof EMPLOYER_DETAIL_SECTION_IDS[number];

/** Labels for the in-page section switcher the profile renders on the iPhone
 *  shell, where the web sidebar (and so the section nav) is not mounted. Same
 *  fallback rule the list's in-page toolbar uses. */
export const EMPLOYER_DETAIL_SECTION_LABELS: Record<EmployerDetailSectionId, string> = {
  'details':         'Details',
  'trading-history': 'Trading History',
  'risk-profile':    'Risk Profile',
  'notes':           'Notes',
  'contacts':        'Contacts',
};

export function isEmployerDetailSectionId(value: unknown): value is EmployerDetailSectionId {
  return typeof value === 'string'
    && (EMPLOYER_DETAIL_SECTION_IDS as readonly string[]).includes(value);
}

export const ENTITY_TYPES_WITH_TRADING_NAME: MainContractorEntityType[] = [
  'sole_trader', 'partnership', 'other',
];

export const ENTITY_TYPES_WITH_PRIMARY_CONTACT: MainContractorEntityType[] = [
  'individual', 'sole_trader',
];
